import React, { createContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { DetailsPage } from '../../../../layout/pages/DetailsPage';
import { PlacementHeader } from '../PlacementDetailsHeader/PlacementHeader';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { placementDetailsAction } from '../../../../../store/redux-store/placement-details/slice';
import { parseInt } from 'lodash';
import { usePromiseTracker } from 'react-promise-tracker';
import CircularProgress from '@mui/material/CircularProgress';
import { makeStyles } from 'tss-react/mui';
import { selectRequiredDataLoadStatus } from '../../../../../store/redux-store/placement-details/selectors';
import ModificationsTab from '../PlacementTabPanel/ModificationsTab/ModificationsTab';
import { DocumentsTab } from '../PlacementTabPanel/DocumentsTab/DocumentsTab';
import { ChangeHistoryTab } from '../PlacementTabPanel/ChangeHistoryTab/ChangeHistoryTab';
import { GlanceDrawer } from '../Edit/Components/GlanceDrawer';
import { PlacementSummaryTab } from '../PlacementTabPanel/PlacementSummaryTab/PlacementSummaryTab';
import { ITaskStickChosen, TaskEntity } from 'app/models/Tasks/Tasks';
import { taskDetailsActions } from 'app/components/Tasks/store/Tasks.redux';
import { useDecision } from '@optimizely/react-sdk';
import { CredentialingTabWrapper } from '../PlacementTabPanel/CredentialingTab/CredentialingTabWrapper';
import { PlacementDetailsStatusWrapper } from './PlacementDetailsStatusWrapper';
import { selectTaskStickChosen, selectUserCuedTo } from 'app/components/Tasks/store/Tasks.selectors';
import { NavigationProfileCycle } from '@AMIEWEB/Common/NavigationProfileCycle';
import { navigationProfilesAction } from '@AMIEWEB/GlobalSearch/store/NavigationProfiles.redux';
import {
  selectNavigationProfilesFlag,
  selectNavigationProfilesItems,
  selectNavigationRowId,
} from '@AMIEWEB/GlobalSearch/store/NavigationProfiles.selectors';
import { PlacementProgressBar } from '../QuickGlanceBar/PlacementProgressBar';
import { ff_peformance_enhanced_placement_profile_page } from 'app/constants/FeatureFlags/Placement/Keys';
import { useInjectReducer, useInjectSaga } from 'redux-injectors';
import { PlacementModsReducer, PlacementModsSliceKey } from 'store/redux-store/placement-mods/slice';
import { placementModsSaga } from 'store/redux-store/placement-mods/saga';
import { GenericTabs } from '@AMIEWEB/Common/Tabs/GenericTabs';
import { BookingDetailReport } from '@AMIEWEB/ReportPages/R448/BookingDetailReport';
import {
  selectLoadingCompletedStatus,
  selectPlacementConsolidatedDetails,
} from 'store/redux-store/placement-summary/consolidatedStoreSelectors';
import { PlacementSummaryTabLoaded } from '../PlacementTabPanel/PlacementSummaryTab/PlacementSummaryTabLoaded';
import queryString from 'query-string';

const useStyles = makeStyles()(theme => ({
  MuiCircularProgressRoot: {
    left: '50%',
    position: 'absolute',
    top: '50vh',
  },
}));

export const PlacementDetailsContext = createContext<{ renderedOnScreen: boolean }>({ renderedOnScreen: false });

export const PlacementDetails = ({ renderedOnScreen = false }) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const requiredDataLoadStatus = useSelector(selectRequiredDataLoadStatus);
  const params = useParams<{ placementId: string }>();
  const { promiseInProgress: headerIsLoading } = usePromiseTracker({ area: 'placement-header-call', delay: 0 });
  const { promiseInProgress: requiredDataLoading } = usePromiseTracker({ area: 'placement-required-data', delay: 0 });
  const [performanceOptimizedPageLoad] = useDecision(ff_peformance_enhanced_placement_profile_page);
  const dispatch = useDispatch();
  const isUserCuedTo = useSelector(selectUserCuedTo);
  const taskStickChosen = useSelector(selectTaskStickChosen);
  const [stickChoosen, setStickChoosen] = useState<ITaskStickChosen>({ entity: TaskEntity.PLACEMENTS, stick: null });

  const [upDisabled, setUpDisabled] = useState<boolean>(false);
  const [downDisabled, setDownDisabled] = useState<boolean>(false);

  const history = useNavigate();
  const location = useLocation();
  const navigationProfiles = useSelector(selectNavigationProfilesItems);
  const navigationFlag = useSelector(selectNavigationProfilesFlag);
  const navigationRowId = useSelector(selectNavigationRowId);
  const [currentIndex, setCurrentIndex] = useState<number | undefined>(undefined);
  useInjectSaga({ key: PlacementModsSliceKey, saga: placementModsSaga });
  useInjectReducer({ key: PlacementModsSliceKey, reducer: PlacementModsReducer });

  const placementConsolidatedDetails = useSelector(selectPlacementConsolidatedDetails);
  const loadingCompletedStatus = useSelector(selectLoadingCompletedStatus);

  React.useEffect(() => {
    const navigationIndex = navigationProfiles.findIndex(s => s.id == navigationRowId);

    setDownDisabled(navigationIndex === navigationProfiles?.length - 1);
    setUpDisabled(navigationIndex === 0);
    setCurrentIndex(navigationIndex);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.placementId, navigationRowId]);

  useEffect(() => {
    setStickChoosen(taskStickChosen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskStickChosen]);

  useEffect(() => {
    dispatch(taskDetailsActions.setCurrentContainer(TaskEntity.PLACEMENTS));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tabs = React.useMemo(() => {
    const flagEnabled = performanceOptimizedPageLoad?.enabled;
    //flagEnabled = false;
    const pageTabs = [
      {
        label: t('placement.profile.tabLabels.summaryTab'),
        component: flagEnabled ? (
          loadingCompletedStatus && placementConsolidatedDetails ? (
            <PlacementSummaryTab />
          ) : (
            <PlacementSummaryTabLoaded />
          )
        ) : (
          <PlacementSummaryTab />
        ),
      },
      { label: t('placement.profile.tabLabels.detailReportTab'), component: <BookingDetailReport /> },
      {
        label: t('placement.profile.tabLabels.credentialingTab'),
        component: <CredentialingTabWrapper />,
      },
      {
        label: t('placement.profile.tabLabels.documentsTab'),
        component: <DocumentsTab />,
      },
      // {
      //   label: t('placement.profile.tabLabels.communicationTab'),
      //   component: <CommunicationTab />,
      // },
      {
        label: t('placement.profile.tabLabels.changeHistoryTab'),
        component: <ChangeHistoryTab placementId={parseInt(params.placementId)} />,
      },
    ];
    pageTabs.splice(3, 0, {
      label: t('placement.profile.tabLabels.modifications'),
      component: <ModificationsTab />,
    });
    return pageTabs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placementConsolidatedDetails, loadingCompletedStatus, performanceOptimizedPageLoad?.enabled]);

  const handleClickUp = () => {
    const nextIdx = navigationProfiles[currentIndex - 1];
    const nextPlacementId = nextIdx?.placementId;
    const currentTab = queryString.parse(window.location.search).tab || '0';
    history(`/placement/${nextPlacementId}?tab=${currentTab}`, { state: 'navigationCycle' });
    dispatch(navigationProfilesAction.setCurrentRowId({ rowId: navigationProfiles[currentIndex - 1]?.id }));
  };

  const handleClickDown = () => {
    const nextIdx = navigationProfiles[currentIndex + 1];
    const nextPlacementId = nextIdx?.placementId;
    const currentTab = queryString.parse(window.location.search).tab || '0';
    history(`/placement/${nextPlacementId}?tab=${currentTab}`, { state: 'navigationCycle' });
    dispatch(navigationProfilesAction.setCurrentRowId({ rowId: navigationProfiles[currentIndex + 1]?.id }));
  };

  return (
    performanceOptimizedPageLoad?.enabled
      ? requiredDataLoadStatus === undefined
      : headerIsLoading || requiredDataLoading || requiredDataLoadStatus === undefined
  ) ? (
    <CircularProgress className={classes.MuiCircularProgressRoot} />
  ) : (
    <DetailsPage
      title={t('placement.profile.title')}
      enablePageActionSpace
      head={<PlacementHeader />}
      progress={<PlacementProgressBar />}
      titleActions={
        <PlacementDetailsStatusWrapper
          key={params.placementId}
          stickIconArray={isUserCuedTo ? [4, 5] : [4, 3]}
          taskStickChosen={stickChoosen}
        />
      }
      navigationActions={
        navigationProfiles?.length > 0 && (navigationFlag || location?.state === 'navigationCycle') ? (
          <NavigationProfileCycle
            handleClickUp={handleClickUp}
            handleClickDown={handleClickDown}
            disabledUp={upDisabled}
            disabledDown={downDisabled}
          />
        ) : null
      }
      currentContainer={TaskEntity.PLACEMENTS}
      taskStickChosen={stickChoosen}
      paddingBottom={'25px'}
    >
      <GenericTabs
        onChange={() => dispatch(placementDetailsAction.setModificationTabRendered(false))}
        tabs={tabs}
        modifyUrl
        addOnComponent={<GlanceDrawer />}
        version1
      />
    </DetailsPage>
  );
};
